// GAVION X3: data quality (Paramètres → Qualité). TS twins of supabase/schemas/32_gavion_data_quality.sql, used by
// the FakeRest demo client (dataQuality/api/localDataQualityClient.ts): duplicate keys and pair rules, the
// "À corriger" predicates and the merge field rules. Same vectors in domain/dataQuality.test.ts and
// supabase/tests/x3_data_quality.test.sql.
import {
  DEFAULT_LEAD_STATUSES,
  isCustomLeadStatus,
  type LeadStatusDef,
} from "./leads/leadStatusModel";
import { normalizePhone } from "./phone";

export type DuplicateReason = "phone" | "name_city" | "website";
export type FixKind =
  | "missing_phone"
  | "invalid_phone"
  | "missing_trade"
  | "missing_region";
export const FIX_KINDS: readonly FixKind[] = [
  "missing_phone",
  "invalid_phone",
  "missing_trade",
  "missing_region",
];

/** Columns the admin picks side by side (merge_companies p_picks). */
export const PICKABLE_FIELDS = [
  "name",
  "phone_number",
  "email",
  "website",
  "address",
  "zipcode",
  "city",
  "state_abbr",
  "region",
  "country",
  "trade",
  "segment",
  "sector",
  "description",
  "linkedin_url",
  "estimated_employees",
  "language",
  "assigned_sales_id",
  "prospect_status",
] as const;
export type PickableField = (typeof PICKABLE_FIELDS)[number];
export type MergePicks = Partial<Record<PickableField, "survivor" | "loser">>;

export type DqCompany = {
  id: number;
  name?: string | null;
  city?: string | null;
  phone_number?: string | null;
  phone_e164?: string | null;
  phone_valid?: boolean | null;
  website?: string | null;
  description?: string | null;
  prospect_status?: string | null;
  trade?: string | null;
  region?: string | null;
  [key: string]: unknown;
};

export type DuplicatePairIds = {
  a: number;
  b: number;
  reasons: DuplicateReason[];
};

const unaccent = (s: string) => s.normalize("NFD").replace(/\p{M}/gu, "");
const blank = (v: unknown) =>
  v === null || v === undefined || String(v).trim() === "";

export const textKey = (v: string | null | undefined): string | null =>
  unaccent((v ?? "").toLowerCase()).replace(/[^a-z0-9]+/g, "") || null;

const LEGAL_FORMS =
  /(^| )(inc|ltee|ltd|limitee|enr|enrg|senc|corp|corporation|cie)(?= |$)/g;
export const nameKey = (v: string | null | undefined): string | null =>
  unaccent((v ?? "").toLowerCase())
    .replace(/[^a-z0-9]+/g, " ")
    .replace(LEGAL_FORMS, " ")
    .replace(LEGAL_FORMS, " ") // adjacent forms ("inc ltee") share a space
    .replace(/ /g, "") || null;

/** Twin of private.website_host (28_gavion_m365.sql). */
export const websiteHost = (v: string | null | undefined): string | null =>
  (v ?? "")
    .trim()
    .replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")
    .replace(/^www\.|[/:?#].*$/gi, "")
    .toLowerCase() || null;

const GENERIC_HOST = new RegExp(
  "(^|\\.)(facebook\\.com|fb\\.com|instagram\\.com|linkedin\\.com|google\\.com|goo\\.gl|g\\.page|" +
    "business\\.site|wixsite\\.com|wix\\.com|squarespace\\.com|weebly\\.com|wordpress\\.com|blogspot\\.com|godaddysites\\.com|" +
    "jimdo\\.com|site123\\.me|square\\.site|myshopify\\.com|shopify\\.com|linktr\\.ee|yelp\\.(com|ca)|pagesjaunes\\.ca|" +
    "yellowpages\\.ca|canpages\\.ca|411\\.ca|cylex\\.ca|hotfrog\\.ca|houzz\\.(com|ca)|homestars\\.com|kijiji\\.ca|bbb\\.org|" +
    "renoassistance\\.ca|soumissionrenovation\\.ca|tiktok\\.com|youtube\\.com|twitter\\.com|x\\.com)$",
);
export const isGenericHost = (host: string | null | undefined) =>
  !!host && GENERIC_HOST.test(host);

/** Keys of a lead or an import row for the import's possible-duplicate rule (88_, the dq_pairs name_city / website rules). */
export type ImportMatchKeys = {
  nk: string | null;
  ck: string | null;
  host: string | null;
};
export const importMatchKeys = (
  v: Readonly<Record<string, unknown>>,
): ImportMatchKeys => {
  const str = (x: unknown) => (x == null ? null : String(x));
  const host = websiteHost(str(v.website));
  return {
    nk: nameKey(str(v.name)),
    ck: textKey(str(v.city)),
    host: host && !isGenericHost(host) ? host : null,
  };
};
/** Twin of import_companies' step 4b: same name key, then same city (or both blank) and / or same site (one city blank). */
export const importMatchReasons = (
  x: ImportMatchKeys,
  y: ImportMatchKeys,
): Exclude<DuplicateReason, "phone">[] => {
  if (!x.nk || x.nk !== y.nk) return [];
  const reasons: Exclude<DuplicateReason, "phone">[] = [];
  if (x.ck === y.ck) reasons.push("name_city");
  if (x.host && x.host === y.host && (x.ck === y.ck || !x.ck || !y.ck))
    reasons.push("website");
  return reasons;
};

const DUPLICATE_NOTE = "Téléphone en double";
export const phoneKey = (
  e164: string | null | undefined,
  description: string | null | undefined,
): string | null => {
  if (e164) return e164;
  if (!description?.startsWith(DUPLICATE_NOTE)) return null;
  const m = / : ([^:]*)$/.exec(description);
  return m ? normalizePhone(m[1]) : null;
};

export const pairKey = (x: number, y: number) =>
  x < y ? `${x}:${y}` : `${y}:${x}`;

/** Twin of private.dq_pairs: pairs (a < b) sorted by a, b; reasons sorted. */
export const findDuplicatePairs = (
  companies: readonly DqCompany[],
  dismissed: ReadonlySet<string>,
): DuplicatePairIds[] => {
  const keys = companies.map((c) => {
    const host = websiteHost(c.website);
    return {
      id: c.id,
      ph: phoneKey(c.phone_e164, c.description),
      nk: nameKey(c.name),
      ck: textKey(c.city),
      host: host && !isGenericHost(host) ? host : null,
    };
  });
  const found = new Map<string, Set<DuplicateReason>>();
  const add = (x: number, y: number, r: DuplicateReason) => {
    const k = pairKey(x, y);
    if (!found.has(k)) found.set(k, new Set());
    found.get(k)!.add(r);
  };
  for (let i = 0; i < keys.length; i++)
    for (let j = i + 1; j < keys.length; j++) {
      const x = keys[i];
      const y = keys[j];
      if (x.ph && x.ph === y.ph) add(x.id, y.id, "phone");
      if (x.nk && x.nk === y.nk) {
        if (x.ck === y.ck) add(x.id, y.id, "name_city"); // same city, or both blank
        if (x.host && x.host === y.host && (x.ck === y.ck || !x.ck || !y.ck))
          add(x.id, y.id, "website");
      }
    }
  return [...found.entries()]
    .filter(([k]) => !dismissed.has(k))
    .map(([k, rs]) => {
      const [a, b] = k.split(":").map(Number);
      return { a, b, reasons: [...rs].sort() };
    })
    .sort((p, q) => p.a - q.a || p.b - q.b);
};

const CLOSED = new Set(["bad_number", "do_not_call"]);
export const needsFix = (kind: FixKind, c: DqCompany): boolean => {
  const open = !CLOSED.has(String(c.prospect_status ?? "new"));
  switch (kind) {
    case "missing_phone":
      return blank(c.phone_number) && open;
    case "invalid_phone":
      return (
        !blank(c.phone_number) &&
        (!c.phone_e164 || c.phone_valid === false) &&
        open
      );
    case "missing_trade":
      return blank(c.trade);
    case "missing_region":
      return blank(c.region);
  }
};

const STATUS_RANK: Record<string, number> = {
  do_not_call: 100,
  customer: 90,
  demo_booked: 80,
  in_pipeline: 80, // GAVION call-brief-pipeline (dq_status_rank, 83_)
  callback: 70,
  in_progress: 60,
  cooling_down: 50,
  not_interested: 40,
  bad_number: 30,
};
// GAVION lead statuses: an added status ranks as in_progress (private.dq_status_rank)
const rank = (s: unknown, statuses: readonly LeadStatusDef[]) =>
  typeof s === "string" && isCustomLeadStatus(statuses, s)
    ? 60
    : (STATUS_RANK[String(s)] ?? 10);

/**
 * Twin of private.dq_effective_status: bad_number describes a phone, so it only counts when the merged lead keeps
 * that record's number; otherwise the record counts as 'new'.
 */
export const effectiveStatus = (
  status: unknown,
  ownE164: unknown,
  finalE164: string | null,
): unknown =>
  status === "bad_number" && (ownE164 ?? null) !== finalE164 ? "new" : status;

type Row = Record<string, unknown>;
const least = (x: unknown, y: unknown) =>
  x == null ? (y ?? null) : y == null ? x : String(x) < String(y) ? x : y;
const greatest = (x: unknown, y: unknown) =>
  x == null ? (y ?? null) : y == null ? x : String(x) > String(y) ? x : y;
const ts = (v: unknown) => (v == null ? null : Date.parse(String(v)));

/**
 * Twin of merge_companies' field rules: the survivor's values after the merge (only the fields it writes).
 * Dates are compared as ISO strings / timestamps, as stored by FakeRest.
 */
export const mergeCompanyFields = (
  s: Row,
  l: Row,
  picks: MergePicks,
  statuses: readonly LeadStatusDef[] = DEFAULT_LEAD_STATUSES,
): Row => {
  const out: Row = {};
  for (const f of PICKABLE_FIELDS) {
    if (f === "prospect_status") continue;
    out[f] =
      picks[f] === "loser"
        ? (l[f] ?? null)
        : picks[f] === "survivor"
          ? (s[f] ?? null)
          : blank(s[f])
            ? (l[f] ?? null)
            : s[f];
  }
  const finalE164 = normalizePhone(out.phone_number);
  const sStatus = effectiveStatus(s.prospect_status, s.phone_e164, finalE164);
  const lStatus = effectiveStatus(l.prospect_status, l.phone_e164, finalE164);
  out.prospect_status =
    s.prospect_status === "do_not_call" || l.prospect_status === "do_not_call"
      ? "do_not_call"
      : picks.prospect_status === "loser"
        ? l.prospect_status
        : picks.prospect_status === "survivor"
          ? s.prospect_status
          : rank(lStatus, statuses) > rank(sStatus, statuses)
            ? lStatus
            : sStatus;
  const lCalled = ts(l.last_called_at);
  const sCalled = ts(s.last_called_at);
  const loserNewer = lCalled != null && (sCalled == null || lCalled > sCalled);
  Object.assign(out, {
    attempt_count: Math.max(
      Number(s.attempt_count ?? 0),
      Number(l.attempt_count ?? 0),
    ),
    created_at: least(s.created_at, l.created_at),
    date_added: least(s.date_added, l.date_added),
    last_called_at: loserNewer ? l.last_called_at : (s.last_called_at ?? null),
    last_call_outcome: loserNewer
      ? (l.last_call_outcome ?? null)
      : (s.last_call_outcome ?? l.last_call_outcome ?? null),
    next_callback_at: least(s.next_callback_at, l.next_callback_at),
    cooldown_until: greatest(s.cooldown_until, l.cooldown_until),
    last_email_at: greatest(s.last_email_at, l.last_email_at),
    fit_score:
      s.fit_score == null
        ? (l.fit_score ?? null)
        : l.fit_score == null
          ? s.fit_score
          : Math.max(Number(s.fit_score), Number(l.fit_score)),
    high_quality: !!s.high_quality || !!l.high_quality,
    tags: [
      ...new Set([
        ...((s.tags as number[] | undefined) ?? []),
        ...((l.tags as number[] | undefined) ?? []),
      ]),
    ].sort((x, y) => x - y),
    locked_by: null,
    locked_until: null,
  });
  return out;
};
